import { useState, useEffect } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { globalCache } from '@/utils/cache';

// 🔥 AUTH CACHE - Load permissions ONCE per session
interface CachedAuthState {
  data: CompleteAuthState | null;
  timestamp: number;
}

const authCache: CachedAuthState = {
  data: null,
  timestamp: 0
};

const CACHE_MAX_AGE = 5 * 60 * 1000; // 5 minutes

const isCacheValid = (): boolean => {
  return !!authCache.data && (Date.now() - authCache.timestamp) < CACHE_MAX_AGE;
};

export const clearAuthCache = () => {
  authCache.data = null;
  authCache.timestamp = 0;
};

export interface MemberProfile {
  id: string;
  user_id: string;
  name: string;
  firstname?: string;
  lastname?: string;
  email: string;
  image?: string;
  subscription_type?: string;
  status: string;
  company?: string;
  role?: string;
  bio?: string;
  phone?: string;
  city?: string;
  country?: string;
  linkedin?: string;
  twitter?: string;
  website?: string;
  skills?: string[];
  interests?: string[];
  offers?: string[];
  needs?: string[];
  languages?: string[];
  industry?: string;
  achievements?: string;
  tags?: string[];
  is_visible_in_directory?: boolean;
  is_team_member?: boolean;
}

export type AppRole = 'super_admin' | 'admin' | 'moderator' | 'user' | null;

export interface CompleteAuthState {
  // Auth
  user: User | null;
  session: Session | null;
  isLoading: boolean;
  isInitialized: boolean;
  
  // Member profile
  memberProfile: MemberProfile | null;
  member: MemberProfile | null; // ✅ Alias for compatibility
  
  // Role
  role: AppRole;
  isAdmin: boolean;
  isSuperAdmin: boolean;
  
  // Permissions
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  
  // Compatibility
  isMember: boolean;
  subscription: string;
  error: any;
  retryAuth: () => void;
  
  // Actions
  signIn: (email: string, password: string) => Promise<{ data: any; error: any }>;
  signOut: () => Promise<void>;
  signUp: (email: string, password: string, metadata?: Record<string, any>) => Promise<{ data: any; error: any }>;
  refreshMemberData: () => void;
  
  // Legacy compatibility
  login: (email: string, password: string) => Promise<{ data: any; error: any }>;
  logout: () => Promise<void>;
  signup: (email: string, password: string, metadata?: Record<string, any>) => Promise<{ data: any; error: any }>;
}

/**
 * Hook unifié qui combine useAuth + useMember + useUserRole
 * 🔥 Performance: 1 seul hook, 2 requêtes parallèles, 1 seul re-render
 */
export const useAuthComplete = (): CompleteAuthState => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
  const [role, setRole] = useState<AppRole>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // 🔥 PHASE 2: Chargement optimisé avec cache global
  const loadCompleteAuthState = async () => {
    try {
      // ✅ Check cache first
      if (isCacheValid()) {
        console.log('⚡ [Auth] Using cached auth state');
        setSession(authCache.data!.session);
        setUser(authCache.data!.user);
        setMemberProfile(authCache.data!.memberProfile);
        setRole(authCache.data!.role);
        setPermissions(authCache.data!.permissions);
        setIsLoading(false);
        setIsInitialized(true);
        return;
      }

      console.log('🔄 [Auth] Loading fresh auth state');
      setIsLoading(true);
      
      // 1. Charger session auth
      const { data: { session: sessionData }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) {
        console.error('❌ [Auth] Session error:', sessionError);
        setIsLoading(false);
        setIsInitialized(true);
        return;
      }

      setSession(sessionData);
      setUser(sessionData?.user || null);

      if (!sessionData?.user) {
        console.log('⚠️ [Auth] No session found');
        setIsLoading(false);
        setIsInitialized(true);
        return;
      }

      console.log('🔍 [Auth] Loading member + permissions (filtered)');

      // 2. Charger le rôle d'abord
      const { data: roleData } = await supabase
        .from('user_roles')
        .select('role')
        .eq('user_id', sessionData.user.id)
        .maybeSingle();

      const userRole = roleData?.role as AppRole || null;
      setRole(userRole);

      // 3. Charger member + permissions en PARALLÈLE
      const [memberResponse, permissionsResponse] = await Promise.all([
        // Member data (depuis cache si possible)
        (async () => {
          const cacheKey = `member_${sessionData.user.id}`;
          const cached = globalCache.get<MemberProfile>(cacheKey);
          if (cached) {
            console.log('🔄 Using cached subscription data from club_members');
            return { data: cached, error: null };
          }

          const { data, error } = await supabase
            .from('club_members')
            .select('id, user_id, name, firstname, lastname, email, image, subscription_type, status, company, role, bio, phone, city, country, linkedin, twitter, website, skills, interests, offers, needs, languages, industry, achievements, tags, is_visible_in_directory, is_team_member')
            .eq('user_id', sessionData.user.id)
            .maybeSingle();

          if (data && !error) {
            globalCache.set(cacheKey, data, 300000); // 5 min cache
          }
          return { data, error };
        })(),

        // Permissions (si admin/super_admin)
        (userRole === 'admin' || userRole === 'super_admin')
          ? supabase
              .from('role_permissions')
              .select('permission')
              .eq('role', userRole)
          : Promise.resolve({ data: null, error: null })
      ]);

      const member = memberResponse.data;
      setMemberProfile(member);

      const permissionsList = permissionsResponse.data?.map(p => p.permission) || [];
      setPermissions(permissionsList);
      
      // ✅ Save to cache
      const newAuthState: CompleteAuthState = {
        user: sessionData.user,
        session: sessionData,
        memberProfile: member,
        member: member, // Alias
        role: userRole || null,
        permissions: permissionsList,
        isLoading: false,
        isInitialized: true,
        isAdmin: userRole === 'admin' || userRole === 'super_admin',
        isSuperAdmin: userRole === 'super_admin',
        isMember: !!member,
        subscription: member?.subscription_type || 'free',
        error: null,
        retryAuth: () => {},
        hasPermission: (permission: string) => permissionsList.includes(permission),
        signIn: async () => ({ data: null, error: null }),
        signOut: async () => {},
        signUp: async () => ({ data: null, error: null }),
        refreshMemberData: () => {},
        login: async () => ({ data: null, error: null }),
        logout: async () => {},
        signup: async () => ({ data: null, error: null })
      };
      
      authCache.data = newAuthState;
      authCache.timestamp = Date.now();
      
      setIsLoading(false);
      setIsInitialized(true);

      console.log('✅ [useAuthComplete] Loaded:', {
        user: sessionData.user.email,
        member: member?.name,
        role: userRole,
        isAdmin: userRole === 'admin' || userRole === 'super_admin',
        isSuperAdmin: userRole === 'super_admin',
        permissions: permissionsList.length
      });

    } catch (error) {
      console.error('❌ [Auth] Error loading auth state:', error);
      setIsLoading(false);
      setIsInitialized(true);
    }
  };

  useEffect(() => {
    let mounted = true;

    const initAuth = async () => {
      if (!mounted) return;
      await loadCompleteAuthState();
    };

    initAuth();

    // Listen to auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('🔔 [Auth] Auth state changed:', event);
      if (!mounted) return;
      
      if (event === 'SIGNED_OUT') {
        clearAuthCache();
        setTimeout(() => {
          if (mounted) {
            loadCompleteAuthState();
          }
        }, 0);
      } else if (event === 'TOKEN_REFRESHED') {
        setTimeout(() => {
          if (mounted) {
            loadCompleteAuthState();
          }
        }, 0);
      }
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  // Actions
  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // ✅ Preload auth state immediately after successful login
    if (data.session) {
      console.log('✅ [Auth] Preloading permissions at login');
      await loadCompleteAuthState();
    }
    
    return { data, error };
  };

  const signOut = async () => {
    clearAuthCache(); // ✅ Clear cache on logout
    await supabase.auth.signOut();
    globalCache.clear();
    setSession(null);
    setUser(null);
    setMemberProfile(null);
    setRole(null);
    setPermissions([]);
    setIsInitialized(false);
  };

  const signUp = async (email: string, password: string, metadata?: Record<string, any>) => {
    const redirectUrl = `${window.location.origin}/`;
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: metadata
      }
    });
    return { data, error };
  };

  const refreshMemberData = () => {
    loadCompleteAuthState();
  };

  return {
    user,
    session,
    memberProfile,
    member: memberProfile, // ✅ Alias for compatibility
    role,
    isAdmin: role === 'admin' || role === 'super_admin',
    isSuperAdmin: role === 'super_admin',
    permissions,
    hasPermission: (permission: string) => permissions.includes(permission),
    isMember: !!memberProfile,
    subscription: memberProfile?.subscription_type || 'free',
    error: null,
    retryAuth: refreshMemberData,
    isLoading,
    isInitialized,
    signIn,
    signOut,
    signUp,
    refreshMemberData,
    // Legacy compatibility
    login: signIn,
    logout: signOut,
    signup: signUp
  };
};
